<template>
  <div class="sequence-exercises-container">
    <vuescroll>
      <v-container class="no-max-width">
        <drag-selector
          :disabled="readonly"
          v-model="checkedList"
          ref="dragSelector"
          @change="handleDragSelectorChange"
        >
          <div
            class="day"
            v-for="day in daysMerged"
            :key="day.id"
            :class="
              isProcedureDateRelated && day.id == 0 ? 'with-procedure' : ''
            "
          >
            <div
              class="day-index"
              :class="isProcedureDateRelated ? 'procedure-related' : ''"
            >
              <div class="day-index--wrapper">
                <div class="day-index--day">
                  Day <span>{{ day.id }}</span>
                </div>
                <a
                  v-if="!readonly"
                  :class="
                    `day-index--toggle-selection ${
                      daysSelected[day.id] === 'selected'
                        ? 'bhRed--text'
                        : 'bhBlue--text'
                    }`
                  "
                  @mousedown.stop="_ => null"
                  @click.stop="handleChangeSelectDay($event, day.id)"
                >
                  {{
                    daysSelected[day.id] === 'selected'
                      ? 'deselect day'
                      : 'select day'
                  }}</a
                >
              </div>
            </div>
            <div class="day-column add" v-if="!readonly">
              <v-tooltip open-delay="200" top>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="new-exercise"
                    v-bind="attrs"
                    v-on="on"
                    @click="$emit('new-exercise', day.id)"
                  >
                    <v-icon size="16">bhi-plus</v-icon>
                  </div>
                </template>
                <span>Add activity</span>
              </v-tooltip>
            </div>
            <div class="day-column paste" v-if="!readonly">
              <v-tooltip open-delay="200" top>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    :class="
                      `paste-exercises ${
                        checkedList.length > 0 ? '' : 'disabled'
                      }`
                    "
                    @mousedown.stop="_ => null"
                    @click.stop="pasteExercises(day.id)"
                  >
                    <v-icon size="16">bhi-content-paste</v-icon>
                  </div>
                </template>
                <span>Paste selected items</span>
              </v-tooltip>
            </div>
            <div class="day-column">
              <draggable
                group="exercises"
                @end="dragEnd($event, day.id)"
                @start="dragStart"
                :disabled="readonly || hasSelectedItems"
                :options="{ filter: '.v-text-field', preventOnFilter: false }"
                :move="checkMove"
                @change="changed(day.id, $event)"
                :list="day.items"
                :key="'column-' + day.id"
                class="day-column-wrap"
              >
                <drag-selector-item
                  componentName="exercise-tile"
                  v-for="(column, id) in day.items"
                  ref="dragSelectorItem"
                  :key="'column-' + day.id + '-' + id"
                  :id="column.id"
                  :title="column.title"
                  :description="column.description"
                  :media="column.media"
                  :additional-media="column.additionalMedia"
                  :mobile-static-media="column.mobileStaticMedia"
                  :multi-thumbnail="
                    column.multiThumbnail ? column.multiThumbnail : null
                  "
                  :type="column.type"
                  :editable="true"
                  :readonly="readonly"
                  :hideMenu="hideMenu"
                  :duplicable="true"
                  :hoverLoader="progressBarsValues[column.id]"
                  :exercise="
                    column.type == 'EXERCISE' ? column.exercise : false
                  "
                  :series="column.type == 'SERIES' ? column.series : false"
                  :time="
                    column.type == 'ACTIVITY' ? column.activity.time : false
                  "
                  :activity="
                    column.type == 'ACTIVITY' ? column.activity : false
                  "
                  :ownership="
                    column.type == 'ACTIVITY' ? column.activity.type : false
                  "
                  :dragSelectorElement="{ ...column, day: day.id }"
                  @mouseover="
                    exerciseOpenHover(
                      column.id,
                      column.media,
                      column.mobileStaticMedia,
                      column.additionalMedia,
                    )
                  "
                  @mouseleave="exerciseClearHover(column.id)"
                  @stopHoverPopup="
                    exerciseClearHover(column.id)
                    exerciseCloseHover()
                  "
                  @mousedown.native.stop="_ => null"
                  @delete="removeExercise"
                  @duplicate="duplicateExercise"
                  @edit="editExercise(column, day.date)"
                  @quick-edit="$emit('quick-edit', column, $event)"
                  @series-quick-edit="$emit('series-quick-edit', $event)"
                />
              </draggable>
            </div>
            <div class="day-column additional"></div>
          </div>
        </drag-selector>
      </v-container>
    </vuescroll>
    <div :class="`pa-4 multi-select-actions active`">
      <v-btn
        class="transparent mr-4"
        text
        color="bhGray"
        rounded
        @click="deselectAllElements"
        v-if="!readonly && hasSelectedItems"
      >
        Deselect all
      </v-btn>
      <v-btn
        class="transparent mr-4"
        text
        color="bhRed"
        rounded
        @click="removeExercises"
        v-if="!readonly && hasSelectedItems"
      >
        Delete
      </v-btn>
      <v-btn
        color="accent"
        rounded
        class="mr-4"
        @click="copyExercises"
        v-if="!readonly && hasSelectedItems"
      >
        Copy
      </v-btn>
      <v-btn
        v-if="!readonly"
        color="accent"
        fab
        rounded
        @click="$emit('show-info-dialog')"
        >?</v-btn
      >
    </div>
    <v-dialog
      v-model="exerciseHover"
      hide-overlay
      attach=".sequence-exercises-container"
      content-class="exercises-hover-dialog"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card @mouseleave.native.stop="exerciseCloseHover()">
        <img
          v-if="exerciseHoverUrl && exerciseHoverType == 'base64'"
          :src="'data:image/jpeg;base64,' + exerciseHoverUrl"
        />
        <img
          v-else-if="exerciseHoverUrl && exerciseHoverType == 'url'"
          :src="exerciseHoverUrl"
        />
        <v-btn color="white" icon size="24" @click="exerciseCloseHover">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import vuescroll from 'vuescroll'
import DragSelector from '@/components/dragSelector/DragSelector.vue'
import DragSelectorItem from '@/components/dragSelector/DragSelectorItem.vue'
import cloneDeep from 'lodash/cloneDeep'

const SEQUENCE_DAYS = 100

import isStringBase64 from '@/helpers/isStringBase64.js'
export default {
  name: 'SequenceExercisesContainer',
  data() {
    return {
      checkedList: [],
      columns: [],
      daysMerged: false,
      daysSelected: {},
      draggedExerciseId: null,
      draggedExerciseList: [],
      draggedExerciseDay: null,
      draggedExerciseChange: false,
      exerciseHover: false,
      exerciseHoverUrl: null,
      exerciseHoverType: 'base64',
      exerciseHoverTimeout: null,
      exerciseHoverInterval: null,
      progressBarsValues: {},
    }
  },
  components: {
    draggable,
    vuescroll,
    DragSelector,
    DragSelectorItem,
  },
  props: {
    days: {
      type: Array,
      default: () => {
        return []
      },
    },
    isProcedureDateRelated: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hideMenu: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {},
  watch: {
    days: {
      handler: function(val) {
        if (val) this.daysMerged = cloneDeep(this.mergeDays())

        this.$nextTick(() => {
          this.daysSelected = this.wholeDaysSelected()
        })
      },
      deep: true,
    },
    checkedList() {
      this.daysSelected = this.wholeDaysSelected()
    },
  },
  computed: {
    daysDefinitions() {
      let arr = []
      let index = 0
      for (let i = 1; i <= SEQUENCE_DAYS; i++) {
        arr[i] = { id: i - index }
      }
      return arr
    },
    hasSelectedItems() {
      return this.checkedList?.length ?? 0
    },
  },
  methods: {
    isStringBase64,
    wholeDaysSelected() {
      const items = this.$refs.dragSelectorItem || []
      const object = {}
      this.daysMerged.forEach(day => {
        let selection = 'notSelected'
        const dayItems = items.filter(
          el => el.dragSelectorElement.day === day.id,
        )
        const selectedItems = dayItems.filter(el => el.selected === true).length
        const notSelectedItems = dayItems.filter(el => el.selected !== true)
          .length
        if (notSelectedItems === 0 && selectedItems > 0) selection = 'selected'
        else if (notSelectedItems > 0 && selectedItems > 0)
          selection = 'partially'
        else if (notSelectedItems === 0 && selectedItems === 0)
          selection = 'noItems'
        object[day.id] = selection
      })
      return object
    },
    handleDragSelectorChange(list = []) {
      console.log('selectedItems', [...list])
    },
    handleChangeSelectDay(ev, dayId, toggle = true, val) {
      let newVal = val === true

      if (toggle) {
        const daySelection = this.daysSelected[dayId]
        const isAllSelected = daySelection === 'selected'
        newVal = !isAllSelected
      }

      const itemsToChange = (this.$refs.dragSelectorItem || []).filter(
        el => el.dragSelectorElement.day === dayId,
      )
      itemsToChange.forEach(el => el.changeSelect(newVal))
    },
    mergeDays() {
      let newDays = cloneDeep(this.days)
      let arr = []
      this.setColumns(0)
      this.daysDefinitions.forEach(el => {
        let tmp = { id: el.id, title: '', items: [], selection: 'noItems' }
        let tmp2 = newDays.find(item => item.day == el.id)
        if (tmp2 && tmp2.items) {
          tmp.items = tmp2.items.map(item => {
            if (item.type == 'EXERCISE') {
              item.title = item.exercise.title
              item.description = item.exercise.description
              item.media = item.exercise.media
              item.additionalMedia = item.exercise.additionalMedia
              item.mobileStaticMedia = item.exercise.mobileStaticMedia
            } else if (item.type == 'SERIES') {
              item.title = item.series.title
              item.media = item.series.media
              item.additionalMedia = item.series.additionalMedia
              item.mobileStaticMedia = item.series.mobileStaticMedia
              item.multiThumbnail = item.series.exerciseDefinitions
            } else {
              item.title = item.activity.title
              item.media = item.activity.media
              item.additionalMedia = item.activity.additionalMedia
              item.mobileStaticMedia = item.activity.mobileStaticMedia
              item.multiThumbnail = item.activity.exerciseDefinitions
            }
            return {
              ...item,
              exercise: item.exercise ? { ...item.exercise } : null,
              series: item.series ? { ...item.series } : null,
              activity: item.activity ? { ...item.activity } : null,
            }
          })
          tmp.title = tmp2.title
          if (tmp.items.length > this.columns.length || !this.columns)
            this.setColumns(tmp.items.length)
        }
        arr.push(tmp)
      })
      return cloneDeep(arr)
    },
    calculateColumns() {
      let max = 0
      this.daysMerged.forEach(el => {
        if (el.items.length > this.columns.length || !this.columns) {
          this.setColumns(el.items.length)
        }
        if (el.items.length > max) max = el.items.length
      })
      if (max > 0 && max < this.columns.length) this.setColumns(max)
    },
    setColumns(length) {
      if (length > 0) {
        this.columns = Array.from(Array(length).keys())
      } else {
        this.columns = false
      }
    },
    formatTime(time) {
      return this.$moment(time, 'HH:mm:ss').format('hh:mm A')
    },
    checkMove: function(evt) {
      this.draggedExerciseId = evt.draggedContext.element.id
      this.draggedExerciseList = evt.relatedContext.list
    },
    dragStart() {
      if (!this.readonly) {
        this.draggedExerciseChange = false
        this.draggedExerciseDay = null
      }
    },
    dragEnd(ev, day) {
      let index = this.draggedExerciseList.findIndex(
        e => e.id === this.draggedExerciseId,
      )
      let data = {}
      if (index > -1) {
        if (index == 0) {
          if (this.draggedExerciseList.length > 1)
            data['itemAfter'] = this.draggedExerciseList[index + 1].id
        } else if (index == this.draggedExerciseList.length - 1) {
          data['itemBefore'] = this.draggedExerciseList[index - 1].id
        } else {
          data['itemBefore'] = this.draggedExerciseList[index - 1].id
          data['itemAfter'] = this.draggedExerciseList[index + 1].id
        }
      }
      data['remove'] = false
      if (this.draggedExerciseDay) data['day'] = this.draggedExerciseDay
      if (this.draggedExerciseChange) {
        this.$emit('move-exercise', this.draggedExerciseId, data)
      }
    },
    changed(id, ev) {
      this.draggedExerciseChange = true
      if (ev.added) this.draggedExerciseDay = id
    },
    removeExercise(data) {
      this.$emit('remove-exercise', data)
    },
    duplicateExercise(data) {
      this.$emit('duplicate-exercise', data)
    },
    openExercisePreviewDialog(exercise) {
      this.$emit('get-exercise-preview', {
        id:
          exercise.type == 'EXERCISE'
            ? exercise.exercise.id
            : exercise.type == 'SERIES'
            ? exercise.series.id
            : exercise.activity.id,
        type: exercise.type,
      })
    },
    editExercise(data, day) {
      this.$emit('edit-exercise', {
        id:
          data.type == 'EXERCISE'
            ? data.exercise.id
            : data.type == 'ACTIVITY'
            ? data.activity.id
            : data.series.id,
        type: data.type,
        data: data,
        day: day,
      })
    },
    deselectAllElements() {
      this.$refs.dragSelector.cancelAllSelect()
    },
    removeExercises() {
      const exercisesIds = this.checkedList.map(el => el.id)
      this.$emit('remove-exercises', exercisesIds)
    },
    copyExercises() {
      this.$emit('copy-exercises', this.checkedList)
    },
    pasteExercises(dayId) {
      if (this.checkedList.length === 0) return
      this.$emit('paste-exercises', {
        start: dayId,
        items: this.checkedList,
      })
    },
    exerciseOpenHover(
      id,
      media = false,
      mobileStaticMedia = null,
      additionalMedia = null,
    ) {
      clearTimeout(this.exerciseHoverTimeout)
      this.exerciseCloseHover()
      const isDragging = this.$refs.dragSelector?.dragging || false

      if (
        !isDragging &&
        id &&
        (media?.thumbnailUrl ||
          media?.previewUrl ||
          additionalMedia?.thumbnailUrl ||
          additionalMedia?.previewUrl ||
          mobileStaticMedia?.mediaUrl)
      ) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.exerciseHoverInterval)
          clearInterval(this.exerciseHoverInterval)
        this.exerciseHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.exerciseHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.exerciseHoverTimeout) clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHoverTimeout = setTimeout(() => {
          this.exerciseHover = null
          if (
            media?.previewUrl ||
            additionalMedia?.previewUrl ||
            mobileStaticMedia?.mediaUrl
          ) {
            this.exerciseHoverUrl =
              media?.previewUrl ||
              additionalMedia?.previewUrl ||
              mobileStaticMedia?.mediaUrl
            this.exerciseHoverType = this.isStringBase64(
              media?.previewUrl ||
                additionalMedia?.previewUrl ||
                mobileStaticMedia?.mediaUrl,
            )
              ? 'base64'
              : 'url'
            this.exerciseHover = true
          }
        }, 1000)
      } else this.exerciseClearHover(id)
    },
    exerciseClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.exerciseHoverInterval)
      if (!this.exerciseHover) {
        clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHover = false
        this.exerciseHoverUrl = null
        this.exerciseHoverType = 'base64'
      }
    },
    exerciseCloseHover() {
      this.exerciseHover = false
    },
    onClickOutside() {
      this.exerciseCloseHover()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.exerciseCloseHover()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables';
.sequence-exercises-container {
  background: $bh-blue--4;
  width: 100%;
  height: calc(100vh - 80px);
  overflow: hidden;
  /* fix resize of vuescroll */
  .__vuescroll {
    width: 100% !important;
    height: 100% !important;
  }
  .container {
    display: table;
    padding-bottom: 80px;

    &.no-max-width {
      max-width: none;
    }
  }
  .__hidebar {
    -ms-overflow-style: none; /* IE 11 */
    scrollbar-width: none;
  }
  .day {
    display: table-row;
    align-items: center;
    position: relative;
    white-space: nowrap;
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      width: 100%;
      border-bottom: 1px solid $bh-white;
      left: 0;
      z-index: 1;
    }
    &.with-procedure {
      position: relative;
      &::after {
        background: $bh-red--light;
        content: '';
        opacity: 0.2;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
      }
      * {
        z-index: 1;
      }
    }
    &-index {
      display: table-cell;
      vertical-align: middle;
      align-items: center;
      white-space: nowrap;
      &.procedure-related {
        width: 120px;
      }
      &--wrapper {
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-items: center;
      }
      &--day {
        text-align: left;
        font-size: 9px;
        padding: 0 5px;
        color: $bh-gray--2;
        span {
          display: block;
          font-weight: bold;
          font-size: 12px;
        }
      }
      &--toggle-selection {
        font-size: 12px;
        display: inline-block;
        min-width: 75px;
        text-align: center;
      }
      &--name {
        width: 150px;
        .bh-text-input--wrap {
          margin-bottom: 0;
        }
        .bh-text-input.active {
          background: $bh-white;
        }
        .v-input {
          font-size: 12px;
        }
      }
      &--procedure {
        color: $bh-white;
        background: $bh-red;
        opacity: 0.8;
        margin-left: 10px;
        margin-right: 10px;
        border-radius: 22px;
        width: 72px;
        text-align: center;
        line-height: 26px;
        font-size: 9px;
        text-transform: uppercase;
        font-weight: bold;
      }
    }
    &-column {
      display: table-cell;
      vertical-align: middle;
      height: 92px;
      width: 99%;
      &-wrap {
        display: flex;
      }
      &.add {
        width: auto;
        padding-left: 4px;
        padding-right: 4px;
        .new-exercise {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          padding: $gutter;
          border-radius: 8px;
          transition-duration: 150ms;
          background: $bh-blue--4;
          color: $bh-gray--3;
          cursor: pointer;
          font-weight: bold;
          padding: 0;
          line-height: 16px;
          .v-icon {
            margin: 10px;
            color: $bh-gray--3;
          }
          &:hover {
            background: $bh-white;
            color: $bh-blue;
            .v-icon {
              color: $bh-blue;
            }
          }
        }
      }
      &.paste {
        width: auto;
        padding-left: 4px;
        padding-right: 4px;
        .paste-exercises {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          padding: $gutter;
          border-radius: 8px;
          transition-duration: 150ms;
          background: $bh-blue--4;
          color: $bh-gray--3;
          cursor: pointer;
          font-weight: bold;
          padding: 0;
          line-height: 16px;
          .v-icon {
            margin: 10px;
            color: $bh-gray--3;
          }
          &:not(.disabled):hover {
            background: $bh-white;
            color: $bh-blue;
            .v-icon {
              color: $bh-blue;
            }
          }
        }
      }
      &.additional {
        width: 0;
        padding-left: 10px;
      }
    }
    .exercise-tile {
      display: flex;
      position: relative;
    }
  }

  .multi-select-actions {
    position: fixed;
    bottom: 0;
    right: 0;
    display: none;
    align-items: center;
    justify-content: flex-end;
    z-index: 2;

    &.active {
      display: flex;
    }
  }
  .exercises-hover-dialog {
    width: auto;
    padding: 10px;
    background: $bh-white;
    .v-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      background: $bh-blue;
    }
    img {
      display: block;
      max-height: 80vh;
      max-width: 1000px;
    }
  }
}
</style>
